<template></template>

<script lang="ts">
import { useHead } from '@vueuse/head';
import type { PropType } from 'vue';
import { defineComponent, onBeforeMount } from 'vue';
import type { IntegrationEspClickdimensionModel } from './Model';
import { getCookie } from '@/src/utilities/CookieHelpers';
import { useCampaignStore } from '@/src/store/campaign';

type ClickDimensionAnalytics = {
  setAccountKey: (accountKey: string) => void;
  setDomain: (domain: string) => void;
  setScore: (score: number | null) => void;
  trackPage: () => void;
};

declare global {
  interface Window {
    clickdimensions: {
      Analytics: new (domain: string) => ClickDimensionAnalytics;
    };

    cdScore?: number;
  }
}

export default defineComponent({
  name: 'integration-esp-clickdimension',
  props: {
    model: {
      type: Object as PropType<IntegrationEspClickdimensionModel>,
      required: true
    }
  },
  setup(props) {
    const campaignStore = useCampaignStore();

    let readyPromiseResolve: (() => void) | undefined;
    const readyPromise = new Promise<void>((resolve) => {
      readyPromiseResolve = resolve;
    });

    const waitForClickdimension = (): Promise<void> => {
      return new Promise<void>((resolve) => {
        if (typeof window.clickdimensions !== 'undefined') {
          resolve();
          return;
        }

        const timer = window.setInterval(() => {
          if (typeof window.clickdimensions !== 'undefined') {
            resolve();
            window.clearInterval(timer);
          }
        }, 1000);
      });
    };

    onBeforeMount(async () => {
      if (props.model.state.domain && props.model.state.accountKey && props.model.state.apiDomain) {
        useHead({
          script: [
            {
              src: `https://${props.model.state.apiDomain}/ts.js`,
              key: 'click-dimension'
            }
          ]
        });

        await waitForClickdimension();

        const cdAnalytics = new window.clickdimensions.Analytics(props.model.state.domain);
        cdAnalytics.setAccountKey(props.model.state.accountKey);
        cdAnalytics.setDomain(props.model.state.domain);
        cdAnalytics.setScore(
          typeof window.cdScore === 'undefined' ? 0 : Number(window.cdScore) === 0 ? null : window.cdScore
        );
        cdAnalytics.trackPage();

        const cusid = getCookie<string>('cusid');

        if (cusid) {
          campaignStore.addStaticFormData({
            cusid
          });
        }
      }

      if (readyPromiseResolve) {
        readyPromiseResolve();
      }
    });

    return {
      onBeforeEnter: async () => {
        await readyPromise;
      }
    };
  }
});
</script>
